import { Stack, Typography, StackProps, Checkbox, Box } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as localState from '../state';
import * as state from '@/components/Analysis/state';
import {
  SavedBlockPoIsQuery,
  usePointOfInterestCuboidsLazyQuery,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { AnalysisCuboid } from '@/types';
import { formatFieldValue } from '@/utils';

// Function to get metadata value from POI data
// TODO: When the backend supports totalArea and totalCountByPoiType on PointOfInterest,
// update the SavedBlockPOIs query and replace this function with real data access
const getMetadataValue = (
  poi: SavedBlockPoIsQuery['pointOfInterests'][0],
  metadataType: string,
  unitSystem: UnitSystemEnum
) => {
  // Get actual values from POI template data
  if (poi.templateData && poi.templateData.length > 0 && poi.template?.fields) {
    // Find the template field that matches the metadata type
    const templateField = poi.templateData.find((field) => {
      // Find the corresponding template field definition
      const fieldDefinition = poi?.template?.fields.find((tf) => tf.id === field.id);
      // Check if field key matches metadata type
      if (fieldDefinition?.key === metadataType) {
        return true;
      }
      // Also check field name for backward compatibility
      if (fieldDefinition?.name?.toLowerCase().includes(metadataType.toLowerCase())) {
        return true;
      }
      return false;
    });
    if (templateField?.value) {
      // Get the field definition to access unit and type
      const fieldDefinition = poi.template?.fields.find((tf) => tf.id === templateField.id);

      // Use formatFieldValue for proper unit conversion like in workscope detail tab
      return formatFieldValue({
        unitSystem,
        type: fieldDefinition?.type,
        unit: fieldDefinition?.unit,
        value: templateField.value,
        showUnitLabel: true,
        roundToDecimal: 2,
      });
    }
  }
};

type Props = {
  poi: SavedBlockPoIsQuery['pointOfInterests'][0];
  onRowClick: () => void;
  selectedMetadata?: string;
} & StackProps;

export const CustomSavedBlocksListItem = ({
  poi,
  onRowClick,
  selectedMetadata = 'area',
  ...rest
}: Props) => {
  const { id, name, poiId } = poi;
  const { showSnackBar } = useSnackBarMessage({});
  const [customBlockCuboids, setCustomBlockCuboids] = useAtom(localState.customBlockCuboids);
  const unitSystem = useAtomValue(state.unitSystem);
  const setShowWorkscopeOverlayIcons = useSetAtom(localState.showWorkscopeOverlayIcons);

  const [fetchCuboids, { loading: cuboidsLoading }] = usePointOfInterestCuboidsLazyQuery();

  const isSelected = customBlockCuboids.has(id ?? '');

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;

    if (!value) {
      setCustomBlockCuboids((current) => {
        const copy = current.asMutableStateMap();
        copy.delete(id);
        return copy.asStateMap();
      });
      return;
    }

    if (!id) {
      showSnackBar('Invalid data. Please contact support', 'error');
      return;
    }

    const response = await fetchCuboids({
      variables: {
        pointOfInterestId: id,
      },
      fetchPolicy: 'cache-first',
    });

    const poiData = response.data?.pointOfInterest;
    const cuboid = poiData?.cuboids?.[0];
    if (!cuboid) {
      showSnackBar('Invalid data. Please contact support', 'error');
      return;
    }

    setCustomBlockCuboids((current) => {
      const newCuboids = current.asMutableStateMap();
      newCuboids.set(id, {
        ...cuboid,
        id,
        name,
        status: poiData?.status,
      } as AnalysisCuboid);
      return newCuboids.asStateMap();
    });
    setShowWorkscopeOverlayIcons(true);
  };

  const metadataValue = getMetadataValue(poi, selectedMetadata, unitSystem);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        p: 1,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        ...rest.sx,
      }}
      onClick={onRowClick}
      {...rest}
    >
      <Checkbox
        checked={isSelected}
        onChange={handleCheckboxChange}
        onClick={(e) => e.stopPropagation()}
        disabled={cuboidsLoading}
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {name} - {poiId}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 'bold',
          color: 'text.primary',
        }}
      >
        {metadataValue}
      </Typography>
    </Stack>
  );
};
